import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCustomArticles } from '../../lib/help/custom';
import { buildHelpIndex, type HelpHit } from '../../lib/help/search';
import { useRole } from '../../lib/session';

export function useHelpSearch() {
  const role = useRole();
  const custom = useCustomArticles();
  return useMemo(() => buildHelpIndex(role, custom), [role, custom]);
}

/** Logs a query that found nothing (debounced), so admins see what help is missing. */
export function useReportMiss(query: string, hits: HelpHit[]) {
  const last = useRef('');
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3 || hits.length || q === last.current) return;
    const t = setTimeout(() => {
      last.current = q;
      fetch('/api/help/search-miss', { method: 'POST', body: JSON.stringify({ query: q }) }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [query, hits]);
}

/** Search bar on top of Onboarding: instant, typo-tolerant, jumps to the matching section. */
export function HelpSearch() {
  const index = useHelpSearch();
  const [q, setQ] = useState('');
  const hits = useMemo(() => index.search(q, 8), [index, q]);
  useReportMiss(q, hits);
  const navigate = useNavigate();
  return (
    <search className="relative">
      <label className="relative block">
        <span className="sr-only">Search help</span>
        <Search className="pointer-events-none absolute start-3 top-2.5 size-4 text-muted-foreground" aria-hidden="true" />
        <input
          type="search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search help (typos are fine)…"
          className="h-9 w-full rounded-md border border-input bg-surface ps-9 pe-3 text-sm placeholder:text-muted-foreground"
        />
      </label>
      {q.trim() && (
        <ul
          aria-label="Help search results"
          className="absolute z-40 mt-1 max-h-96 w-full overflow-auto rounded-md border bg-surface-raised p-1 shadow-md"
        >
          {hits.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">No articles match. We've noted the search so the help can improve.</li>
          )}
          {hits.map(h => (
            <li key={h.id}>
              <a
                href={`/onboarding/${h.slug}${h.section ? `#${h.section}` : ''}`}
                onClick={e => {
                  e.preventDefault();
                  setQ('');
                  navigate({ to: '/onboarding/$slug', params: { slug: h.slug }, hash: h.section });
                }}
                className="block rounded px-3 py-2 hover:bg-muted"
              >
                <span className="text-sm font-medium">
                  {h.title}
                  {h.heading && <span className="text-muted-foreground"> › {h.heading}</span>}
                </span>
                <span className="block truncate text-xs text-muted-foreground">{h.snippet}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </search>
  );
}
